import React, { useState, useEffect, useCallback } from 'react';
import {
  ChartBarIcon,
  TableCellsIcon,
  ArrowPathIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';

// Import chart components
import AnalyticsOverview from './AnalyticsOverview';
import RequestsTimelineChart from './RequestsTimelineChart';
import StatusCodeChart from './StatusCodeChart';
import GeoDistributionChart from './GeoDistributionChart';
import TopEndpointsTable from './TopEndpointsTable';
import DeviceBrowserCharts from './DeviceBrowserCharts';
import ProjectComparisonChart from './ProjectComparisonChart';

/**
 * AnalyticsDashboard - Main dashboard with toggle between charts and table view
 *
 * Props:
 * - project: Selected project filter ('all', 'iot-dashboard', etc.)
 * - timeRange: Time range filter ('1h', '6h', '24h', '7d') - optional, uses internal state if not provided
 * - hideHeader: If true, hides the internal header (used when parent provides unified header)
 * - onToggleView: Callback when view toggle is clicked (legacy)
 * - showTable: Whether table view is shown (legacy)
 */
const AnalyticsDashboard = ({
  project = 'all',
  timeRange: externalTimeRange,
  customRange = null,
  hideHeader = false,
  onToggleView,
  showTable = false
}) => {
  // State for all analytics data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Analytics data state
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [deviceData, setDeviceData] = useState(null);
  const [projectComparison, setProjectComparison] = useState([]);

  // Filter state - use external timeRange if provided, otherwise internal state
  const [internalTimeRange, setInternalTimeRange] = useState('24h');
  const timeRange = externalTimeRange || internalTimeRange;
  const setTimeRange = externalTimeRange ? () => {} : setInternalTimeRange;

  /**
   * Fetch all analytics data
   */
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build params with custom range support
      const params = { project, timeRange };
      if (customRange?.start && customRange?.end) {
        params.startDate = customRange.start;
        params.endDate = customRange.end;
      }

      // Fetch all data in parallel
      const [
        overviewRes,
        timeseriesRes,
        statusRes,
        geoRes,
        endpointsRes,
        devicesRes,
        projectsRes
      ] = await Promise.all([
        axiosInstance.get('/logs/nginx/analytics/overview', { params }),
        axiosInstance.get('/logs/nginx/analytics/timeseries', { params: { ...params, interval: '1h' } }),
        axiosInstance.get('/logs/nginx/analytics/status', { params }),
        axiosInstance.get('/logs/nginx/analytics/geo', { params }),
        axiosInstance.get('/logs/nginx/analytics/endpoints', { params }),
        axiosInstance.get('/logs/nginx/analytics/devices', { params }),
        project === 'all' ? axiosInstance.get('/logs/nginx/analytics/projects', { params: { timeRange } }) : Promise.resolve({ data: { success: true, data: [] } })
      ]);

      // Update state with fetched data
      if (overviewRes.data.success) setOverview(overviewRes.data.data);
      if (timeseriesRes.data.success) setTimeseries(timeseriesRes.data.data || []);
      if (statusRes.data.success) setStatusData(statusRes.data.data);
      if (geoRes.data.success) setGeoData(geoRes.data.data);
      if (endpointsRes.data.success) setEndpoints(endpointsRes.data.data || []);
      if (devicesRes.data.success) setDeviceData(devicesRes.data.data);
      if (projectsRes.data.success) setProjectComparison(projectsRes.data.data || []);

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [project, timeRange, customRange]);

  // Initial fetch and refresh
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    const interval = setInterval(fetchAnalytics, 120000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  return (
    <div className="space-y-4">
      {/* Header with controls - only show if hideHeader is false */}
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-800">Traffic Analytics</h2>
            {lastUpdated && (
              <span className="text-xs text-gray-500 ml-2">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Time range filter */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <FunnelIcon className="w-4 h-4 text-gray-500 ml-1" />
              {['1h', '6h', '24h', '7d'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className={`px-2 py-1 text-xs font-medium rounded ${
                    timeRange === range
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>

            {/* Refresh button */}
            <button
              onClick={fetchAnalytics}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>

            {/* View toggle */}
            {onToggleView && (
              <button
                onClick={onToggleView}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {showTable ? (
                  <>
                    <ChartBarIcon className="w-4 h-4" />
                    <span>Charts</span>
                  </>
                ) : (
                  <>
                    <TableCellsIcon className="w-4 h-4" />
                    <span>Table</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
          <button
            onClick={fetchAnalytics}
            className="ml-2 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Analytics content */}
      <div className="space-y-4">
        {/* Overview stats */}
        <AnalyticsOverview data={overview} loading={loading} />

        {/* Main charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RequestsTimelineChart data={timeseries} loading={loading} />
          <StatusCodeChart data={statusData} loading={loading} />
        </div>

        {/* Project comparison (only when viewing all projects) */}
        {project === 'all' && projectComparison.length > 0 && (
          <ProjectComparisonChart data={projectComparison} loading={loading} />
        )}

        {/* Secondary charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <GeoDistributionChart data={geoData} loading={loading} />
          <TopEndpointsTable data={endpoints} loading={loading} />
        </div>

        {/* Device and browser charts */}
        <DeviceBrowserCharts data={deviceData} loading={loading} />
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
